import { useEffect, useRef } from 'react';

import { useRealtime } from './RealtimeProvider';
import { RealtimeEvent } from './types';

export type EventHandler<TEvent extends RealtimeEvent = RealtimeEvent> = (
  event: TEvent,
) => void | Promise<void>;

export type EventHandlerMap = {
  [eventType: string]: EventHandler;
};

export interface UseRealtimeEventHandlerOptions {
  topic: string | string[] | null;
  handlers: EventHandlerMap;
  filter?: (event: RealtimeEvent) => boolean;
  deps?: any[];
}

/**
 * Generic hook for subscribing to realtime topics and handling events.
 *
 * Abstracts the common pattern of:
 * 1. Subscribing to topic(s)
 * 2. Listening for events
 * 3. Routing events to handlers by type
 * 4. Cleanup on unmount
 *
 * @example
 * useRealtimeEventHandler({
 *   topic: `events:${eventId}`,
 *   handlers: {
 *     'event.perspective_added': handlePerspectiveAdded,
 *     'event.perspective_removed': handlePerspectiveRemoved,
 *   },
 *   deps: [handlePerspectiveAdded, handlePerspectiveRemoved],
 * });
 */
export function useRealtimeEventHandler({
  topic,
  handlers,
  filter,
  deps = [],
}: UseRealtimeEventHandlerOptions) {
  const { client } = useRealtime();

  const handlersRef = useRef(handlers);
  useEffect(() => {
    handlersRef.current = handlers;
  }, [handlers]);

  useEffect(() => {
    if (!client || !topic) {
      console.log('[useRealtimeEventHandler] No client or topic:', { client: !!client, topic });
      return;
    }

    const topics = Array.isArray(topic) ? topic : [topic];
    console.log('[useRealtimeEventHandler] Subscribing to topics:', topics);

    const unsubscribe = client.onEvent((event: RealtimeEvent) => {
      console.log('[useRealtimeEventHandler] Event received:', event.type, event);

      if (filter && !filter(event)) {
        return;
      }

      const handler = handlersRef.current[event.type];
      if (handler) {
        console.log('[useRealtimeEventHandler] Handler found for:', event.type);
        try {
          handler(event);
        } catch (error) {
          console.error(
            `[useRealtimeEventHandler] Error handling event ${event.type}:`,
            error,
          );
        }
      }
    });

    client.subscribe(topics);

    return () => {
      client.unsubscribe(topics);
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, topic, filter, ...deps]);
}
